import { z } from "zod";
import { metaSchema, sortBySchema } from "./commun.zod";
import { ForfaitSchema } from "./forfait.zod";

export const GroupeBaseSchema = z.object({
  name: z.string().min(1, "Le nom est requis").max(255, "Le nom ne peut pas dépasser 255 caractères").nullable().optional(),
  groupe_type: z.string().max(255, "Le type de groupe ne peut pas dépasser 255 caractères").nullable().optional(),
  is_init: z.boolean().nullable().optional(),
  licence: z.number().int().nullable().optional(),
  is_stripped: z.boolean().nullable().optional(),
  sub_id: z.string().max(255, "L'ID de souscription ne peut pas dépasser 255 caractères").nullable().optional(),
  customer_id: z.string().max(255, "L'ID client ne peut pas dépasser 255 caractères").nullable().optional(),
  date_echeance: z.date().nullable().optional(),
  date_subscribed: z.date().nullable().optional(),
  siret: z.string().max(255, "Le SIRET ne peut pas dépasser 255 caractères").nullable().optional(),
  num_certification: z.string().max(255, "Le numéro de certification ne peut pas dépasser 255 caractères").nullable().optional(),
  cp: z.string().max(5, "Le code postal ne peut pas dépasser 5 caractères").nullable().optional(),
  ville: z.string().max(100, "La ville ne peut pas dépasser 100 caractères").nullable().optional(),
  img_logo: z.string().max(255, "L'URL du logo ne peut pas dépasser 255 caractères").nullable().optional(),
  address_societe: z.string().max(255, "L'adresse de la société ne peut pas dépasser 255 caractères").nullable().optional(),
  name_societe: z.string().max(100, "Le nom de la société ne peut pas dépasser 100 caractères").nullable().optional(),
  forfait_id: z.number().int().positive("L'ID du forfait doit être un nombre positif").nullable().optional(),

  // Nouveaux champs d'abonnement
  plan_type: z.enum(['BASIC', 'ADVANCED', 'FREE']).default('BASIC').optional(),
  max_parcours_per_month: z.number().int().positive().default(50).optional(),
  current_month_parcours: z.number().int().min(0).default(0).optional(),
  subscription_status: z.enum(['ACTIVE', 'INACTIVE', 'TRIAL', 'CANCELLED', 'EXPIRED']).default('TRIAL').optional(),
  tier_id: z.string().optional(),
  free_parcours_used: z.boolean().default(false).optional(),
  free_parcours_type: z.string().nullable().optional(),

  forfait: ForfaitSchema
});

export const GroupeSchema = GroupeBaseSchema.extend({
  id: z.number().int().positive(),
  created_at: z.date().nullable().optional(),
  updated_at: z.date().nullable().optional(),
});

export const GroupeCreateSchema = GroupeBaseSchema.pick({
  name: true,
  groupe_type: true,
  is_init: true,
  licence: true,
  is_stripped: true,
  sub_id: true,
  customer_id: true,
  date_echeance: true,
  date_subscribed: true,
  siret: true,
  num_certification: true,
  cp: true,
  ville: true,
  img_logo: true,
  address_societe: true,
  name_societe: true,
  forfait_id: true,
  // Subscription fields allowed on create (admin-only on API)
  plan_type: true,
  max_parcours_per_month: true,
  current_month_parcours: true,
  subscription_status: true,
  tier_id: true,
  free_parcours_used: true,
  free_parcours_type: true,
});

export const GroupeUpdateSchema = z.object({
  id: z.number().int().positive(),
  name: z.string().min(1, "Le nom est requis").max(255, "Le nom ne peut pas dépasser 255 caractères").nullable().optional(),
  groupe_type: z.string().max(255, "Le type de groupe ne peut pas dépasser 255 caractères").nullable().optional(),
  is_init: z.boolean().nullable().optional(),
  licence: z.number().int().nullable().optional(),
  is_stripped: z.boolean().nullable().optional(),
  sub_id: z.string().max(255, "L'ID de souscription ne peut pas dépasser 255 caractères").nullable().optional(),
  customer_id: z.string().max(255, "L'ID client ne peut pas dépasser 255 caractères").nullable().optional(),
  date_echeance: z.date().nullable().optional(),
  date_subscribed: z.date().nullable().optional(),
  siret: z.string().max(255, "Le SIRET ne peut pas dépasser 255 caractères").nullable().optional(),
  num_certification: z.string().max(255, "Le numéro de certification ne peut pas dépasser 255 caractères").nullable().optional(),
  cp: z.string().max(5, "Le code postal ne peut pas dépasser 5 caractères").nullable().optional(),
  ville: z.string().max(100, "La ville ne peut pas dépasser 100 caractères").nullable().optional(),
  img_logo: z.string().max(255, "L'URL du logo ne peut pas dépasser 255 caractères").nullable().optional(),
  address_societe: z.string().max(255, "L'adresse de la société ne peut pas dépasser 255 caractères").nullable().optional(),
  name_societe: z.string().max(100, "Le nom de la société ne peut pas dépasser 100 caractères").nullable().optional(),
  forfait_id: z.number().int().positive("L'ID du forfait doit être un nombre positif").nullable().optional(),
  // Nouveaux champs d'abonnement (mises à jour)
  plan_type: z.enum(['BASIC', 'ADVANCED', 'FREE']).optional(),
  max_parcours_per_month: z.number().int().positive().optional(),
  current_month_parcours: z.number().int().min(0).optional(),
  subscription_status: z.enum(['ACTIVE', 'INACTIVE', 'TRIAL', 'CANCELLED', 'EXPIRED']).optional(),
  tier_id: z.string().optional(),
  free_parcours_used: z.boolean().optional(),
  free_parcours_type: z.string().nullable().optional(),
});

// Enriched plan details schema for groupe detail response
export const GroupePlanDetailsSchema = z.object({
  planType: z.enum(['BASIC', 'ADVANCED', 'FREE']),
  tierId: z.string(),
  tier: z.object({
    id: z.string(),
    title: z.string().optional(),
    subTitle: z.string().optional(),
    maxTrees: z.number(),
    price: z.number(),
    benefits: z.array(z.string()),
    benefitsHtml: z.string().optional(),
  }),
  limits: z.object({
    maxParcoursPerMonth: z.number(),
  }),
  usage: z.object({
    currentMonthParcours: z.number(),
  }),
  status: z.enum(['ACTIVE', 'INACTIVE', 'TRIAL', 'CANCELLED', 'EXPIRED']),
});

export const GroupeListResponseSchema = z.object({
  data: z.array(GroupeSchema),
  meta: metaSchema
});

export const GroupeResponseSchema = z.object({
  data: GroupeSchema,
  message: z.string().optional(),
});

export const GroupeSelectSchema = z.object({
  id: z.number().int().positive().optional(),
  limit: z.number().default(10),
  page: z.number().default(1),
  search: z.string().optional(),
  name: z.string().optional(),
  groupe_type: z.string().optional(),
  ville: z.string().optional(),
  name_societe: z.string().optional(),
  siret: z.string().optional(),
  forfait_id: z.number().int().positive().optional(),
  is_init: z.boolean().optional(),
  is_stripped: z.enum(['all', 'stripped', 'non-stripped']).default('all').optional(),
  sortBy: sortBySchema
});

export type Groupe = z.infer<typeof GroupeSchema>;
export type GroupeCreateInput = z.infer<typeof GroupeCreateSchema>;
export type GroupeUpdateInput = z.infer<typeof GroupeUpdateSchema>;
export type GroupeSelectInput = z.infer<typeof GroupeSelectSchema>;
export type GroupeListResponse = z.infer<typeof GroupeListResponseSchema>;
export type GroupeResponse = z.infer<typeof GroupeResponseSchema>;
export type GroupePlanDetails = z.infer<typeof GroupePlanDetailsSchema>;

// Form-specific types for frontend
export type GroupeFormData = Omit<GroupeCreateInput, 'is_init' | 'is_stripped'> & {
  is_init?: boolean;
  is_stripped?: boolean;
};

export type GroupeEditFormData = Omit<GroupeUpdateInput, 'id'>;

// Utility types for different UI states
export type GroupeDisplayItem = Pick<Groupe, 'id' | 'name' | 'name_societe' | 'ville' | 'groupe_type'>;
export type GroupeSelectOption = Pick<Groupe, 'id' | 'name' | 'name_societe'>;
export type GroupeTableItem = Pick<Groupe, 'id' | 'name' | 'name_societe' | 'ville' | 'groupe_type' | 'siret' | 'created_at' | 'updated_at'>;

export const validateGroupeCreate = (data: unknown) => {
  return GroupeCreateSchema.safeParse(data);
};

export const validateGroupeUpdate = (data: unknown) => {
  return GroupeUpdateSchema.omit({ id: true }).safeParse(data);
};

export const validateGroupeSelect = (data: unknown) => {
  return GroupeSelectSchema.safeParse(data);
};
